import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback, useSupabaseLoader } from "../../server/supabase/hooks";
import type { UUID } from "../../utils/uuid";
import { SupabaseDigitalAssetTagService } from "../assets/server/digital-asset-tag-service";
import { useTags } from "./hooks/use-tags";
import { TagManager } from "./tag-manager";
import type { Tag } from "./types";

export const AssetTagModal: FC<{
  open: boolean;
  assetId: UUID;
  onClose: () => void;
}> = ({ open, assetId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [localTags, setLocalTags] = useState<Tag[]>([]);

  const { data: availableTags, loading: availableTagsLoading } = useTags();

  const { loading: assetTagsLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!assetId) return;

      const { data, error } = await new SupabaseDigitalAssetTagService(supabase).getAssetTags(
        account_id,
        assetId,
      );

      if (error) {
        toast.error("Could not load asset tags");
        return;
      }

      const assetTags = data.map((x) => x.tag);
      setLocalTags(assetTags);
      return assetTags;
    },
    [assetId],
  );

  const handleSave = useSupabaseCallback(
    async ({ supabase }) => {
      setLoading(true);
      const { error } = await new SupabaseDigitalAssetTagService(supabase).setAssetTags(
        assetId,
        localTags.map((tag) => tag.id),
      );

      if (error) {
        toast.error("Failed to save tags");
      }

      setLoading(false);
      toast.success("Tags saved successfully");
      onClose();
    },
    [assetId, onClose, localTags],
  );

  return (
    <Dialog open={open}>
      <DialogTitle>Add tags to your file?</DialogTitle>
      <DialogContent sx={{ minWidth: 500, minHeight: 140 }}>
        {!assetTagsLoading && !availableTagsLoading ? (
          <TagManager
            localTags={localTags ?? []}
            setLocalTags={setLocalTags}
            availableTags={availableTags ?? undefined}
          />
        ) : (
          <Box sx={{ py: 2 }}>
            <Skeleton animation="wave" variant="rounded" height={56} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-analytics-id="edit-content-save-continue-without-tags">
          Cancel
        </Button>
        <Button
          data-testid="add-tags-modal-button"
          onClick={() => void handleSave()}
          data-analytics-id="edit-content-save-add-tags"
          loading={loading}
        >
          Add tags
        </Button>
      </DialogActions>
    </Dialog>
  );
};
